import apiClient from './api';
import { handleApiError } from '../utils/errorHandler';
import { API_BASE_URL } from '../config/constants';
import axios from 'axios';

export interface ProductApprovalParams {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
  fromDate?: string;
  toDate?: string;
  storeId?: string;
}

export interface ApproveProductParams {
  comment?: string;
}

export interface RejectProductParams {
  rejectionReason: string;
}

// نوع داده برای درخواست تایید محصول
export interface IProductApproval {
  _id: string;
  name: string;
  price: number;
  status: string;
  createdAt: string;
  storeId: {
    _id: string;
    name: string;
    slug: string;
    logo?: string;
  };
  accountTypeId?: {
    _id: string;
    name: string;
  };
  slug?: string;
  description?: string;
  discountPrice?: number;
  images?: string[];
  thumbnail?: string;
  tags?: string[];
  stock?: number;
  sku?: string;
  sales?: number;
  views?: number;
  isDeleted?: boolean;
  deletedAt?: string | null;
  isVerified?: boolean;
  fields?: any;
  // اطلاعات وریفای محصول
  verificationInfo?: {
    approvedBy?: {
      _id: string;
      name: string;
    };
    rejectedBy?: {
      _id: string;
      name: string;
    };
    approvedAt?: string;
    rejectedAt?: string;
    approvalNote?: string;
    rejectionReason?: string;
  };
  // فیلدهای مورد نیاز برای صفحه جزئیات محصول
  productId?: string;
  productDetails?: {
    name: string;
    price: number;
    discountPrice?: number;
    images: string[];
    description: string;
    stock: number;
    sku?: string;
    accountType: {
      id: string;
      name: string;
    };
    fields: any;
  };
  auditLogs?: any[];
}

// دریافت لیست درخواست‌های تایید محصول
export const getProductApprovals = async (page = 1, limit = 10) => {
  try {
    console.log('Calling getProductApprovals directly with:', { page, limit, status: 'pending_review' });

    // برای اطمینان از دریافت مستقیم داده‌ها بدون پردازش توسط اینترسپتورها
    const response = await axios.get(`${API_BASE_URL}/admin/products`, {
      params: {
        page,
        limit,
        status: 'pending_review',
      },
      headers: {
        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
      },
    });

    console.log('Direct API response:', response);

    // استخراج داده‌ها مستقیم از پاسخ
    const responseData = response.data;
    console.log('Response data structure:', responseData);

    let products = [];
    let pagination = {
      total: 0,
      page: 1,
      pages: 1,
      limit: 10,
    };

    // بررسی ساختار داده برای استخراج صحیح
    if (responseData && responseData.status === 'success') {
      if (responseData.data && responseData.data.products) {
        products = responseData.data.products;
        pagination = responseData.data.pagination || pagination;
      } else if (Array.isArray(responseData.data)) {
        products = responseData.data;
      } else if (responseData.products) {
        products = responseData.products;
        pagination = responseData.pagination || pagination;
      }
    }

    console.log('Extracted products:', products);
    console.log('Extracted pagination:', pagination);

    return {
      approvals: products,
      pagination: pagination,
    };
  } catch (error) {
    console.error('Error fetching product approvals:', error);
    return { approvals: [], pagination: { total: 0, page: 1, pages: 1, limit: 10 } };
  }
};

// تایید درخواست محصول
export const approveProduct = async (productId: string, note?: string) => {
  try {
    console.log(`Approving product with ID: ${productId}, note: ${note}`);
    const response = await apiClient.patch<any>(`/admin/products/${productId}/review`, {
      isVerified: true,
      status: 'active',
      approvalNote: note,
    });
    console.log('Approve product response:', response);
    // برگرداندن داده‌ها با ساختار مناسب
    return response.data?.data?.product || response.data?.data || response.data;
  } catch (error) {
    console.error('Error approving product:', error);
    throw error;
  }
};

// رد درخواست محصول
export const rejectProduct = async (productId: string, reason: string) => {
  try {
    console.log(`Rejecting product with ID: ${productId}, reason: ${reason}`);
    const response = await apiClient.patch<any>(`/admin/products/${productId}/review`, {
      isVerified: false,
      status: 'rejected',
      rejectionReason: reason,
    });
    console.log('Reject product response:', response);
    // برگرداندن داده‌ها با ساختار مناسب
    return response.data?.data?.product || response.data?.data || response.data;
  } catch (error) {
    console.error('Error rejecting product:', error);
    throw error;
  }
};

// دریافت جزئیات یک درخواست
export const getProductApprovalDetails = async (productId: string) => {
  try {
    console.log(`Getting details for product with ID: ${productId}`);
    const response = await apiClient.get<any>(`/admin/products/${productId}`);
    console.log('Product details response:', response);

    // اطمینان از دسترسی به داده‌ها در مسیر صحیح
    return response.data?.data || response.data;
  } catch (error) {
    console.error('Error fetching product approval details:', error);
    throw error;
  }
};

// دریافت محصولات با فیلترهای مختلف
export const getProductsWithFilter = async (filter: string, page = 1, limit = 10) => {
  try {
    console.log(`Getting products with filter: ${filter}`);
    let queryParams: any = { page, limit };

    // اعمال فیلتر بر اساس status محصول
    switch (filter) {
      case 'pending_review':
        queryParams.status = 'pending_review';
        break;
      case 'active':
        queryParams.status = 'active';
        queryParams.isVerified = true; // به صورت boolean ارسال می‌کنیم
        break;
      case 'rejected':
        queryParams.status = 'rejected';
        break;
      case 'inactive':
        queryParams.status = 'inactive';
        break;
      case 'out_of_stock':
        queryParams.status = 'out_of_stock';
        break;
      case 'all':
        // هیچ فیلتری اعمال نمی‌کنیم تا همه محصولات را بازگرداند
        break;
      default:
        // اگر فیلتر نامعتبر باشد، از همه محصولات استفاده می‌کنیم
        break;
    }

    console.log('Using query params:', queryParams);

    // استفاده از axios مستقیم برای اطمینان از ارسال پارامترها به درستی
    const response = await axios.get(`${API_BASE_URL}/admin/products`, {
      params: queryParams,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
      },
    });

    console.log(`Filter ${filter} response:`, response);

    // استخراج داده‌ها از پاسخ API
    const responseData = response.data;

    let products = [];
    let pagination = {
      total: 0,
      page: 1,
      pages: 1,
      limit: 10,
    };

    if (responseData && responseData.status === 'success') {
      if (responseData.data && responseData.data.products) {
        products = responseData.data.products;
        pagination = responseData.data.pagination || pagination;
      }
    }

    console.log('Extracted products:', products);
    console.log('Extracted pagination:', pagination);

    // بررسی اطلاعات تاییدکننده/ردکننده محصولات برای دیباگ
    if (products.length > 0) {
      products.forEach((product: any, index: number) => {
        if (product.verificationInfo) {
          console.log(`Product ${index} - VerificationInfo:`, {
            approvedBy: product.verificationInfo.approvedBy,
            rejectedBy: product.verificationInfo.rejectedBy,
            approvedAt: product.verificationInfo.approvedAt,
            rejectedAt: product.verificationInfo.rejectedAt,
          });
        }

        if (product.verifiedBy) {
          console.log(`Product ${index} - VerifiedBy:`, product.verifiedBy);
        }
      });
    }

    // برگرداندن ساختار داده برای سازگاری با فرانت‌اند
    return {
      approvals: products,
      pagination: pagination,
    };
  } catch (error) {
    console.error(`Error fetching products with filter ${filter}:`, error);
    return { approvals: [], pagination: { total: 0, page: 1, pages: 1, limit: 10 } };
  }
};

// دریافت همه محصولات بدون فیلتر خاص
export const getAllProducts = async (page = 1, limit = 10) => {
  try {
    console.log(`Getting all products with page ${page}, limit ${limit}`);

    const response = await axios.get(`${API_BASE_URL}/admin/products`, {
      params: { page, limit },
      headers: {
        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
      },
    });

    console.log('All products response:', response);

    // استخراج داده‌ها از پاسخ API
    const responseData = response.data;

    let products = [];
    let pagination = {
      total: 0,
      page: 1,
      pages: 1,
      limit: 10,
    };

    if (responseData && responseData.status === 'success') {
      if (responseData.data && responseData.data.products) {
        products = responseData.data.products;
        pagination = responseData.data.pagination || pagination;
      }
    }

    console.log('Extracted products:', products);
    console.log('Extracted pagination:', pagination);

    return {
      approvals: products,
      pagination: pagination,
    };
  } catch (error) {
    console.error('Error fetching all products:', error);
    return { approvals: [], pagination: { total: 0, page: 1, pages: 1, limit: 10 } };
  }
};

// متد آزمایشی که داده‌های نمونه برمی‌گرداند
export const getMockProductApprovals = async () => {
  console.log('Loading mock product approvals');
  // داده‌های نمونه برای تست نمایش در فرانت‌اند
  const mockData = {
    approvals: [
      {
        _id: '68160179d4186900e1796221',
        name: 'سرم ویتامین C ۳۰ میلی‌لیتر',
        price: 1200000,
        status: 'active',
        createdAt: '2025-05-03T11:43:53.748Z',
        storeId: {
          _id: '680f321a4169732b88ab66af',
          name: 'کازماشاپ',
          slug: 'store-1745850619195-1011',
        },
      },
    ],
    pagination: {
      total: 1,
      page: 1,
      pages: 1,
      limit: 10,
    },
  };

  return mockData;
};

// دریافت لیست وضعیت‌های محصول از بک‌اند
export const getProductStatuses = async () => {
  try {
    console.log('Fetching product statuses from backend');
    const response = await apiClient.get<any>(`/admin/products/statuses`);
    console.log('Product statuses response:', response);

    // بررسی ساختار داده‌ها و استخراج وضعیت‌ها
    const statuses = response.data?.data?.statuses || response.data?.statuses || response.data || [];

    return statuses;
  } catch (error) {
    console.error('Error fetching product statuses:', error);
    // وضعیت‌های پیش‌فرض در صورت خطا
    return [
      { key: 'pending_verification', title: 'در انتظار بررسی' },
      { key: 'verified', title: 'تایید شده' },
      { key: 'rejected', title: 'رد شده' },
      { key: 'active', title: 'فعال' },
      { key: 'inactive', title: 'غیرفعال' },
    ];
  }
};

// جلوگیری از انتشار محصول
export const unpublishProduct = async (productId: string, reason?: string) => {
  try {
    console.log(`Unpublishing product with ID: ${productId}, reason: ${reason}`);
    const response = await apiClient.patch<any>(`/admin/products/${productId}/unpublish`, {
      isPublished: false,
      status: 'inactive',
      unpublishReason: reason,
    });
    console.log('Unpublish product response:', response);
    // برگرداندن داده‌ها با ساختار مناسب
    return response.data?.data?.product || response.data?.data || response.data;
  } catch (error) {
    console.error('Error unpublishing product:', error);
    throw error;
  }
};

// به‌روزرسانی محصول
export const updateProduct = async (productId: string, productData: Partial<IProductApproval>) => {
  try {
    console.log(`Updating product with ID: ${productId}`, productData);

    // تهیه داده‌های مورد نیاز برای API
    const updateData = {
      name: productData.name,
      description: productData.description,
      price: productData.price,
      discountPrice: productData.discountPrice,
      stock: productData.stock,
      sku: productData.sku,
      fields: productData.fields,
    };

    const response = await apiClient.patch<any>(`/admin/products/${productId}`, updateData);
    console.log('Update product response:', response);

    // برگرداندن داده‌های به‌روز شده با ساختار مناسب
    return response.data?.data?.product || response.data?.data || response.data;
  } catch (error) {
    console.error('Error updating product:', error);
    throw error;
  }
};

const productApprovalService = {
  /**
   * دریافت تاریخچه تغییرات یک درخواست
   */
  getApprovalHistory: async (id: string) => {
    const response = await apiClient.get<any>(`/admin/products/${id}/history`);
    // برگرداندن داده‌ها با ساختار مناسب
    return response.data?.data || response.data;
  },

  /**
   * متد آزمایشی برای دریافت همه محصولات
   */
  getAllProducts,

  /**
   * متد آزمایشی برای دریافت محصولات با فیلترهای مختلف
   */
  getProductsWithFilter,

  /**
   * دریافت وضعیت‌های محصول از بک‌اند
   */
  getProductStatuses,

  /**
   * جلوگیری از انتشار محصول
   */
  unpublishProduct,

  /**
   * تبدیل مقادیر ID به مقادیر قابل نمایش برای فیلدهای محصول
   */
  getFieldDisplayValue: async (fieldName: string, fieldValue: any, fieldInfo?: any): Promise<string> => {
    try {
      // اگر اطلاعات فیلد موجود نباشد یا فیلد مقدار نداشته باشد
      if (!fieldValue || fieldValue === '') {
        return 'بدون مقدار';
      }

      // اگر مقدار یک آرایه باشد (چند انتخابی)
      if (Array.isArray(fieldValue)) {
        if (fieldValue.length === 0) return 'بدون مقدار';

        // اگر آرایه‌ای از آبجکت‌ها باشد (مثلاً با label و value)
        if (typeof fieldValue[0] === 'object' && fieldValue[0].label) {
          return fieldValue.map((item) => item.label).join('، ');
        }

        // اگر آرایه‌ای از رشته‌ها یا اعداد باشد
        return fieldValue.join('، ');
      }

      // اگر مقدار یک آبجکت باشد با فیلد label
      if (typeof fieldValue === 'object' && fieldValue !== null) {
        if (fieldValue.label) {
          return fieldValue.label;
        }

        if (fieldValue.name) {
          return fieldValue.name;
        }

        // اگر value وجود داشته باشد و خودش یک آبجکت با label باشد
        if (fieldValue.value) {
          if (typeof fieldValue.value === 'object' && fieldValue.value !== null && fieldValue.value.label) {
            return fieldValue.value.label;
          }
          return String(fieldValue.value);
        }

        // سایر حالت‌های آبجکت
        return String(fieldName.replace(/_/g, ' '));
      }

      // اگر مقدار ساده باشد (رشته یا عدد)
      return String(fieldValue);
    } catch (error) {
      console.error('Error formatting field value:', error);
      return String(fieldValue) || 'خطا در نمایش مقدار';
    }
  },

  /**
   * به‌روزرسانی محصول
   */
  updateProduct,
};

export default productApprovalService;
